import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { GraphAction } from '@/components/langgraph-chat/types'

type GraphActionHandler = (action: GraphAction) => void

interface AgentSidebarContextValue {
  open: boolean
  setOpen: (v: boolean) => void
  toggle: () => void
  /** Register a per-route graph-action handler (e.g. set by CaseLayout). */
  registerGraphActionHandler: (h: GraphActionHandler | null) => void
  /** Stable dispatcher — forwards to whatever handler is currently registered. */
  dispatchGraphAction: GraphActionHandler
  /** Thread ID requested by an external caller (e.g. "Open thread" on the
   * hypothesis detail page). The AgentSidebar reads this and sets it as
   * its active thread, then clears the request. Bumped via `openWithThread`. */
  requestedThreadId: string | undefined
  /** Imperative — opens the sidebar and asks it to display the given
   * thread. Pass `undefined` to just open with the current/default thread. */
  openWithThread: (threadId?: string) => void
  /** Cleared by AgentSidebar after it consumes a request. */
  consumeThreadRequest: () => void
  requestedPrompt: { caseId: string; text: string } | undefined
  openWithPrompt: (caseId: string, text: string) => void
  consumePromptRequest: () => void
}

const AgentSidebarContext = createContext<AgentSidebarContextValue | null>(null)

export function AgentSidebarProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [requestedThreadId, setRequestedThreadId] = useState<string | undefined>(undefined)
  const [requestedPrompt, setRequestedPrompt] = useState<{ caseId: string; text: string }>()
  const openWithPrompt = useCallback((caseId: string, text: string) => {
    setRequestedThreadId(undefined)
    setRequestedPrompt({ caseId, text })
    setOpen(true)
  }, [])
  const consumePromptRequest = useCallback(() => setRequestedPrompt(undefined), [])
  const toggle = useCallback(() => setOpen(v => !v), [])
  const handlerRef = useRef<GraphActionHandler | null>(null)
  const pendingRef = useRef<GraphAction | null>(null)

  const registerGraphActionHandler = useCallback((h: GraphActionHandler | null) => {
    handlerRef.current = h
    if (h && pendingRef.current) {
      const pending = pendingRef.current
      pendingRef.current = null
      // Let the handler finish any post-mount setup first.
      setTimeout(() => h(pending), 50)
    }
  }, [])

  const dispatchGraphAction = useCallback<GraphActionHandler>(action => {
    if (handlerRef.current) handlerRef.current(action)
    else pendingRef.current = action
  }, [])

  const openWithThread = useCallback((threadId?: string) => {
    setRequestedThreadId(threadId)
    setOpen(true)
  }, [])

  const consumeThreadRequest = useCallback(() => {
    setRequestedThreadId(undefined)
  }, [])

  const value = useMemo(
    () => ({
      open,
      setOpen,
      toggle,
      registerGraphActionHandler,
      dispatchGraphAction,
      requestedThreadId,
      openWithThread,
      consumeThreadRequest,
      requestedPrompt,
      openWithPrompt,
      consumePromptRequest,
    }),
    [
      open,
      toggle,
      registerGraphActionHandler,
      dispatchGraphAction,
      requestedThreadId,
      openWithThread,
      consumeThreadRequest,
      requestedPrompt,
      openWithPrompt,
      consumePromptRequest,
    ]
  )
  return <AgentSidebarContext.Provider value={value}>{children}</AgentSidebarContext.Provider>
}

export function useAgentSidebar(): AgentSidebarContextValue {
  const v = useContext(AgentSidebarContext)
  if (!v) {
    return {
      open: false,
      setOpen: () => {},
      toggle: () => {},
      registerGraphActionHandler: () => {},
      dispatchGraphAction: () => {},
      requestedThreadId: undefined,
      openWithThread: () => {},
      consumeThreadRequest: () => {},
      requestedPrompt: undefined,
      openWithPrompt: () => {},
      consumePromptRequest: () => {},
    }
  }
  return v
}
